import { type ComponentType, lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import type { ConfigResponse, MeResponse } from '../shared/api.ts';
import type { User } from '../shared/events.ts';
import { isTabType, type TabType } from '../shared/tabs.ts';
import { ConversationView } from './canvas/ConversationView.tsx';
import { api } from './lib/api.ts';
import { MocksBadge } from './profile/MocksPanel.tsx';
import { ProfileView } from './profile/ProfileView.tsx';
import { SignIn } from './SignIn.tsx';
import { type AdminMode, useAdminMode } from './shell/adminMode.ts';
import { clearMocks, setAccountShown, setMocksAllowed, useMocks } from './shell/mocks.ts';
import { TabBar, type TabId } from './shell/TabBar.tsx';
import { MemoryProvider, useMemory } from './shell/useMemory.tsx';
import { InterviewTab } from './tabs/InterviewTab.tsx';
import { ProgressTab } from './tabs/ProgressTab.tsx';

// Set when this tab changes the account the browser is on (the test-account switch, an admin's sign-in or sign-out),
// so the browser's other tabs reload (a `storage` event) rather than keep acting on the account it left.
const ACCOUNT_KEY = 'fuse.account';
function announceAccount() {
  try {
    localStorage.setItem(ACCOUNT_KEY, String(Date.now()));
  } catch {
    // Storage blocked: other tabs find out from a refused admin write or mocked call start, or on reload.
  }
}

// Loaded only when opened: the Lab builds every fixture state, which regular users never need.
const Lab = lazy(() => import('./lab/Lab.tsx').then((m) => ({ default: m.Lab })));
const AdminPage = lazy(() => import('./admin/AdminPage.tsx').then((m) => ({ default: m.AdminPage })));

export function App() {
  return <Main />;
}

function Main() {
  const [config, setConfig] = useState<ConfigResponse | null>(null);
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [isAdmin, setIsAdmin] = useState(false);
  const [flags, setFlags] = useState<MeResponse['flags']>({});
  const [sandbox, setSandbox] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mode = useAdminMode(isAdmin);
  const adminOn = Boolean(mode.admin?.on);
  // Mocks write what they produce like the real thing, so they apply on the test account only (ADR-0014).
  useEffect(() => setMocksAllowed(adminOn && sandbox), [adminOn, sandbox]);
  useEffect(() => setAccountShown(isAdmin ? (sandbox ? 'test' : 'own') : null), [isAdmin, sandbox]);
  const onLab = window.location.pathname.startsWith('/lab') || window.location.pathname === '/dev/catalog';
  const showLab = onLab && adminOn;
  const onAdminPage = window.location.pathname === '/admin' || window.location.pathname.startsWith('/admin/');
  const showAdminPage = onAdminPage && adminOn;

  useEffect(() => {
    Promise.all([api.config(), api.me()])
      .then(([c, me]) => {
        setConfig(c);
        setUser(me.user);
        setIsAdmin(me.isAdmin);
        setFlags(me.flags ?? {});
        setSandbox(me.sandbox);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  // The test account changes `user.id`, so the memory below is reloaded for whichever account is now in use.
  const switchSandbox = useCallback(async (on: boolean) => {
    const me = await api.adminSandbox(on);
    if (!me.user) return;
    setUser(me.user);
    setFlags(me.flags);
    setSandbox(me.sandbox);
    announceAccount();
  }, []);
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === ACCOUNT_KEY) window.location.reload();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  // The test account is an admin-mode tool: with the mode off, the app is the real account, as users see it.
  useEffect(() => {
    if (sandbox && user && !adminOn) void switchSandbox(false).catch(() => {});
  }, [sandbox, user, adminOn, switchSandbox]);
  const admin: AdminMode | null = mode.admin && { ...mode.admin, sandbox, setSandbox: switchSandbox };

  // The Lab is an admin tool: anyone else (or an admin with admin mode off) lands on the app instead.
  useEffect(() => {
    if (user && onLab && !showLab) window.history.replaceState(null, '', '/');
  }, [user, onLab, showLab]);
  // So is the admin page (ADR-0014); the server answers its routes for admins only either way.
  useEffect(() => {
    if (user && onAdminPage && !showAdminPage) window.history.replaceState(null, '', '/');
  }, [user, onAdminPage, showAdminPage]);

  const onUser = useCallback((u: User, admin: boolean, f: MeResponse['flags']) => {
    setUser(u);
    setIsAdmin(admin);
    setFlags(f ?? {});
    setSandbox(false); // sign-in starts on the real account
    if (admin) announceAccount();
  }, []);

  if (error) return <p className="p-8 text-bad">{error}</p>;
  if (!config || user === undefined) return <div className="min-h-dvh" data-testid="loading" />;
  if (!user) return <SignIn config={config} onUser={onUser} />;
  // The Lab (mock data only) replaces the old /dev/catalog.
  if (showLab) {
    return (
      <Suspense fallback={<div className="min-h-dvh" />}>
        <Lab config={config} />
      </Suspense>
    );
  }
  if (showAdminPage) {
    return (
      <Suspense fallback={<div className="min-h-dvh" />}>
        <AdminPage />
      </Suspense>
    );
  }
  return (
    <MemoryProvider key={user.id}>
      <Shell
        user={user}
        admin={admin}
        checkInOn={flags.reassessment === 'on'}
        onSignOut={() =>
          void api.signOut().then(() => {
            // The next person in this browser starts with no admin mode and no mocks.
            mode.set(false);
            clearMocks();
            setUser(null);
            if (isAdmin) announceAccount();
            setIsAdmin(false);
            setFlags({});
            setSandbox(false);
          })
        }
      />
    </MemoryProvider>
  );
}

// Each tab type's view. A Record, so a type added to the catalogue doesn't build until it has one.
const TAB_VIEWS: Record<TabType, ComponentType<{ onPractise: () => void }>> = {
  progress: ProgressTab,
  interview: InterviewTab,
};

const pathFor = (t: TabId) => (t === 'chat' ? '/' : t === 'profile' ? '/profile' : `/t/${t}`);
function tabFromPath(path: string): TabId {
  if (path === '/profile') return 'profile';
  const generated = path.match(/^\/t\/([a-z]+)$/)?.[1];
  return generated && isTabType(generated) ? generated : 'chat';
}

function Shell({
  user,
  admin,
  checkInOn,
  onSignOut,
}: {
  user: User;
  admin: AdminMode | null;
  /** Check-ins (flow 002) are on for this user: home offers one when it's due. */
  checkInOn: boolean;
  onSignOut: () => void;
}) {
  const { memory, loaded, problem } = useMemory();
  const generated = memory.tabs.map((t) => t.type).filter(isTabType);
  const [picked, setTab] = useState<TabId>(() => tabFromPath(window.location.pathname));
  const [live, setLive] = useState(false);
  const unknownTab = picked !== 'chat' && picked !== 'profile' && loaded && !generated.includes(picked);
  // During a call only Chat shows (a back gesture must not hide a live call, whose tab bar is hidden). A link to a
  // tab this user doesn't have shows Chat, and so does a generated tab's link while memory won't load (Chat says why);
  // the link is kept for when it does. Profile still opens: sign out is there.
  const tab: TabId = live || unknownTab || (problem && picked !== 'profile') ? 'chat' : picked;

  useEffect(() => {
    // Drop the dead link, so the view doesn't jump there later when the tab does open.
    if (unknownTab) {
      window.history.replaceState(null, '', `/${window.location.search}`);
      setTab('chat');
    }
  }, [unknownTab]);

  const liveRef = useRef(live);
  liveRef.current = live;
  useEffect(() => {
    const onPop = () => {
      // Back during a call stays on the call (and on /), instead of switching the moment the call ends.
      if (liveRef.current) {
        window.history.pushState(null, '', `/${window.location.search}`);
        return;
      }
      setTab(tabFromPath(window.location.pathname));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const select = useCallback((t: TabId) => {
    // Keep test/debug query params (?transport=..., ?audio=..., ?debug=1) when switching tabs.
    window.history.pushState(null, '', pathFor(t) + window.location.search);
    setTab(t);
  }, []);

  const mocks = useMocks();
  const View = isTabType(tab) ? TAB_VIEWS[tab] : null;

  return (
    <>
      {/* Mocks work on the test account only. It shows even with admin mode off, until the app has left it. */}
      {admin?.sandbox ? (
        <MocksBadge
          mocks={admin.on ? mocks : null}
          sandbox={admin.sandbox}
          inCall={live}
          onOpen={() => select('profile')}
        />
      ) : null}
      {/* The conversation stays mounted while other tabs are open, so a call is never torn down by navigation. */}
      <div hidden={tab !== 'chat'}>
        <ConversationView
          user={user}
          checkInOn={checkInOn}
          onLiveChange={setLive}
          onOpenProfile={() => select('profile')}
        />
      </div>
      {tab === 'profile' ? <ProfileView user={user} admin={admin} onSignOut={onSignOut} /> : null}
      {/* Generated tabs wait for memory: before it loads there is nothing true to show. */}
      {View && loaded ? <View onPractise={() => select('chat')} /> : null}
      {live ? null : <TabBar active={tab} onSelect={select} generated={generated} adminOn={Boolean(admin?.on)} />}
    </>
  );
}
